<template>
  <div class="officers col-xl-5 mx-auto mt-4">
    <h5>Officers for <b>{{ entity.entityName }}</b></h5>
    <p v-if="!officers.length">You don't have any officers. Click the button below to add one.</p>
    <div v-else>
      <b-table v-if="officerDocument" :items="[document]" :fields="officerDocumentFields" class="table-light">
        <template v-slot:cell(formattedCreatedDate)="data">
          {{ moment(data.item.created).format('MM/DD/YYYY') }}
        </template>
        <template slot="actions" slot-scope="data">
          <b-button
            size="sm"
            download
            @click="downloadOfficerDocument(data.item.documentID)"
            variant="success">View</b-button>
          <b-button 
            @click.prevent.stop="deleteClicked(data.item.officerID)" 
            variant="danger" 
            size="sm" 
            class="ml-1"
            v-if="!isViewOnly">Delete</b-button>
        </template>
      </b-table>
      <b-table v-else striped :items="officers" :fields="fields" class="table-light">
        <template v-slot:cell(officerType)="data">
          {{ officerTypeDescription(data.item.officerTitleID) }}
        </template>
        <template v-slot:cell(interestAndType)="data">
          {{ data.item.interest }}{{ data.item.interestType == 1 ? " shr." : '%' }}
        </template>
        <template v-slot:cell(actions)="data">
          <b-button
            :to="{ name: 'officer/edit', params: { submissionId: submission.submissionID, changeId: change.changeID, entityId: entity.entityID, officerId: data.item.officerID }}"
            size="sm"
            variant="success">{{ isViewOnly ? 'View' : 'Edit' }}</b-button>
          <b-button 
            @click.prevent.stop="deleteClicked(data.item.officerID)" 
            variant="danger" 
            size="sm" 
            class="ml-1"
            v-if="!isViewOnly">Delete</b-button>
        </template>
      </b-table>
    </div> 
    <b-button
      :to="{ name: 'officer/edit', params: { submissionId: submission.submissionID, changeId: change.changeID, entityId: entity.entityID } }"
      variant="success"
      v-if="!isViewOnly && !officerDocument">{{ this.officers.length ? 'Add Additional Officer' : 'Add Officer' }}</b-button>
    <span v-if="!isViewOnly && !officers.length" class="mx-3">- OR -</span>
    <b-button
      @click="showDocumentModal"
      variant="success"
      v-if="!isViewOnly && !officers.length"
    >Upload List of Officers</b-button>
    <b-alert show variant="danger" v-if="officerErrors.length" class="mt-5 text-left">
      <h4 class="mb-0">Error{{ officerErrors.length == 1 ? '' : 's' }}</h4>
      <hr class="mt-1">
      <ul>
        <li v-for="(oe, i) in officerErrors" :key="i">
          {{ oe }}
        </li>
      </ul>
    </b-alert>

    <b-modal ref="document-modal" title="Upload a list of officers" header-text-variant="dark" body-text-variant="dark" centered>
      <template slot="default">
        <b-form-group label="File" :state="IsFieldValid($v.file)" :invalid-feedback="RequiredMessage('File', $v.file)">
          <b-form-file accept=".pdf, .txt, .doc, .docx, .xls, .xlsx, .jpg, .jpeg, .png" v-model="$v.file.$model" :state="IsFieldValid($v.file)" placeholder="Choose a file..." drop-placeholder="Drop file here..."></b-form-file>
        </b-form-group>
      </template>
      <template slot="modal-footer" class="float-right">
        <b-button variant="success" @click="uploadClicked" class="ml-1">Upload Document</b-button>
        <b-button variant="secondary" @click="hideDocumentModal">Cancel</b-button>
      </template>
    </b-modal>

    <b-modal ref="delete-modal" title="Are you sure?" header-text-variant="dark" body-text-variant="dark" centered>
      <template slot="default">
        Are you sure you want to delete this officer?
      </template>
      <template slot="modal-footer" class="float-right">
        <b-button variant="danger" @click.prevent="deleteOfficer" :disabled="isDeleting">
          <span v-if="!isDeleting">Delete</span>
          <span v-else><b-spinner small></b-spinner> Please Wait...</span>
        </b-button>
        <b-button variant="secondary" @click.prevent="cancelClicked">Cancel</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import OfficerService from '../services/OfficerService.js'
import BaseService from '../services/BaseService.js'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'Officers',
  data() {
    return {
      isDeleting: false,
      officerErrors: [],
      fields: [
        { key: 'officerName' , label: 'Name', sortable: true, thStyle: { width: '50%' } },
        { key: 'officerType' , label: 'Title', sortable: true, thStyle: { width: '20%' } },
        { key: 'interestAndType' , label: 'Interest', sortable: true, thStyle: { width: '15%' } },
        { key: 'actions', label: '', sortable: false, class: ['action-buttons'] }
      ],
      officerDocumentFields: [
        { key: 'fileName' , label: 'File Name', sortable: true, thStyle: { width: '30%' } },
        { key: 'formattedCreatedDate' , label: 'Created', sortable: true, thStyle: { width: '15%' } },
        { key: 'actions', label: '', sortable: false, class: ['action-buttons'] }
      ],
      officerIdToDelete: '',
      file: ''
    }
  },
  validations: {
    file: {
      required
    }
  },
  methods: {
    downloadOfficerDocument(id) {
      this.error = false
      BaseService.getFileDownload(`/Document/${id}`).then(response => {
        this.DownloadFile(response)
      }).catch(() => {
        this.error = true
      })
    },
    showDocumentModal() {
      this.$v.$reset()
      this.$refs['document-modal'].show()
    },
    hideDocumentModal() {
      this.$refs['document-modal'].hide()
    },
    uploadClicked() {
      this.$v.$touch()
      if (this.$v.$invalid) return

      this.$store.dispatch('officer/saveDocument', { file: this.file, changeID: this.change.changeID, entityID: this.entity.entityID }).then(() => {
        this.hideDocumentModal()
        this.file = ''
      })
    },
    officerTypeDescription(id) {
      return this.officerTitles.find(o => o.officerTitleID == id).description
    },
    deleteClicked(id) {
      this.$refs['delete-modal'].show()
      this.officerIdToDelete = id
    },
    cancelClicked() {
      this.$refs['delete-modal'].hide()
      this.officerIdToDelete = ''
    },
    deleteOfficer() {
      this.isDeleting = true
      this.$store.dispatch('officer/delete', this.officerIdToDelete).then(() => {
        this.isDeleting = false
        this.$refs['delete-modal'].hide()
        this.officerIdToDelete = ''
      }).catch(() => {
        this.isDeleting = false
      })
    },
    backClicked() {
      this.$router.push({
        name: 'entity/edit',
        params: {
          submissionId: this.submission.submissionID,
          changeId: this.change.changeID,
          entityId: this.entity.entityID
        }
      })
    },
    nextClicked() {
      if (this.isViewOnly) {
        this.$router.push({
          name: 'entity/list',
          params: {
            submissionId: this.submission.submissionID,
            changeId: this.change.changeID
          }
        })
      } else {
        OfficerService.validate(this.entity.entityID).then(() => {
          this.$router.push({
            name: 'entity/list',
            params: {
              submissionId: this.submission.submissionID,
              changeId: this.change.changeID
            }
          })
        }).catch(res => {
          this.officerErrors = res.response.data
        })
      }
    }
  },
  computed: {
    ...mapGetters({
      officers: "officer/officersForEntity",
      isViewOnly: 'application/isChangeViewOnly',
      officerDocument: 'document/officerDocument'
    }),
    ...mapState({
      submission: state => state.submission.submission,
      change: state => state.change.change,
      entity: state => state.entity.entity,
      changeTypes: state => state.application.changeTypes,
      officerTitles: state => state.application.officerTitles,
      documents: state => state.document.documents
    }),
    document() {
      return !this.officerDocument || this.documents.find(d => d.documentID == this.officerDocument.documentID)
    }
  },
  created() {
    this.$EventBus.$on('back-clicked', this.backClicked)
    this.$EventBus.$on('next-clicked', this.nextClicked)
  },
  beforeDestroy() {
    this.$EventBus.$off('back-clicked', this.backClicked)
    this.$EventBus.$off('next-clicked', this.nextClicked)
  }
}
</script>